import React from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { Box } from '@mui/material'
import { LanguageUtils, PlusButton } from '@pbt/pbt-ui-components'

import type { InventoryOrderItem } from '~/api/graphql/generated/types'
import { getInventoryOrdersIsUpdating } from '~/store/duck/inventoryOrders'
import { getPackageType, getProductSizeUnit } from '~/store/reducers/constants'

import type { ReceiptFieldGroup } from './hooks/useReceiptFields'
import { OrderItemReceipt } from './OrderItemReceipt'

interface OrderItemReceiptsListProps {
  addDraftReceipt: () => void
  deleteDraftReceipt: (id: string) => void
  draftReceiptFields: ReceiptFieldGroup[]
  inventoryOrderItem: InventoryOrderItem
  isViewOnly?: boolean
  receiptFields: ReceiptFieldGroup[]
}

export const OrderItemReceiptsList = ({
  addDraftReceipt,
  deleteDraftReceipt,
  draftReceiptFields,
  inventoryOrderItem,
  isViewOnly = false,
  receiptFields,
}: OrderItemReceiptsListProps) => {
  const { t } = useTranslation('Common')
  const isUpdating = useSelector(getInventoryOrdersIsUpdating)
  const ProductSizeUnit = useSelector(getProductSizeUnit)
  const PackageType = useSelector(getPackageType)

  const productSizeUnit = LanguageUtils.getConstantTranslatedName(
    inventoryOrderItem.packageUnits.id,
    ProductSizeUnit,
  )
  const packageType = LanguageUtils.getConstantTranslatedName(
    inventoryOrderItem.packageType.id,
    PackageType,
  )
  const perPackageLabel = `${packageType} (${inventoryOrderItem.perPackageAmount})`

  const handlePlusButtonClick = () => {
    if (isUpdating) {
      return
    }

    addDraftReceipt()
  }

  const renderReceipts = (receipts: ReceiptFieldGroup[], isDraft?: boolean) =>
    receipts.map(({ id, fields }, index) => {
      const canDelete =
        !isViewOnly &&
        !isUpdating &&
        isDraft &&
        (receiptFields.length > 0 || index > 0)
      return (
        <OrderItemReceipt
          canDelete={canDelete}
          fields={fields}
          isDraft={isDraft}
          isViewOnly={isViewOnly}
          key={id}
          orderedQuantity={inventoryOrderItem.orderedQuantity}
          perPackageAmount={inventoryOrderItem.perPackageAmount}
          perPackageLabel={perPackageLabel}
          productSizeUnit={productSizeUnit}
          onDelete={() => deleteDraftReceipt(id)}
        />
      )
    })

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      {renderReceipts(receiptFields)}
      {renderReceipts(draftReceiptFields, true)}
      {!isViewOnly && (
        <PlusButton
          label={t('Common:ADD_ROW')}
          sx={{ width: 'fit-content' }}
          onClick={handlePlusButtonClick}
        />
      )}
    </Box>
  )
}
